import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Car, Loader2, Sun, Moon } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@motocraft.com");
  const [password, setPassword] = useState("ChangeMe!2026");
  const [busy, setBusy] = useState(false);

  const formatErr = (d) => {
    if (!d) return "Login failed";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
    return d.msg || String(d);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(u.must_change_password ? "/change-password" : "/");
    }
    catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      {/* Brand panel */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-slate-900" />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-400 rounded-full blur-3xl opacity-20" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-emerald-400 rounded-full blur-3xl opacity-10" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
              <Car size={20} strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-none">MotoCraft</div>
              <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 mt-1">Dealer Management System</div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-5">
              Run your dealership.<br/>
              <span className="text-blue-200">Acquisition to delivery.</span>
            </h2>
            <p className="text-white/70 text-sm max-w-md leading-relaxed">
              Inventory, auctions, repairs, customers, sales, invoicing and live P&L — one operating system built for independent used-car dealers.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "10+", l: "Modules" },
                { v: "3", l: "User roles" },
                { v: "24/7", l: "Live P&L" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-2xl font-bold">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.15em] opacity-60 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[10px] text-white/40 uppercase tracking-[0.2em]">// MOTOCRAFT • {new Date().getFullYear()}</div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative">
        <button onClick={toggle} className="absolute top-6 right-6 p-2 rounded-lg hover:bg-secondary text-muted-foreground transition-colors">
          {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <form onSubmit={submit} className="w-full max-w-md animate-fade-up" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Car size={22} className="text-white" />
            </div>
            <div className="font-display font-bold text-xl">MotoCraft</div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-sm text-muted-foreground mt-1.5">Sign in to manage your dealership.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-3.5 py-3 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 text-sm transition"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-3.5 py-3 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 text-sm transition"
                required
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              Sign in
            </button>
          </div>

          <div className="mt-8 p-4 rounded-xl bg-secondary/70 border border-border/60 text-xs text-muted-foreground">
            <div className="font-semibold mb-1.5 text-foreground">Demo credentials</div>
            <div>Admin: <code className="font-mono text-blue-600 dark:text-blue-400">admin@motocraft.com</code> / <code className="font-mono text-blue-600 dark:text-blue-400">ChangeMe!2026</code></div>
            <div className="mt-1 text-[11px] opacity-80">You&apos;ll be required to set a new password on first login.</div>
          </div>
        </form>
      </div>
    </div>
  );
}
